
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useCart } from '../../../context/CartContext';
import { useQuickView } from '../../../context/QuickViewContext';
import { shopPackages } from '../../../mocks/shopProducts';

export default function ShopPackages() {
  const { ref, isVisible } = useScrollAnimation();
  const { addToCart } = useCart();
  const { openQuickView } = useQuickView();

  const handleAdd = (pkg: typeof shopPackages[0]) => {
    addToCart({
      id: `package-${pkg.id}`,
      name: pkg.name,
      price: pkg.price,
      originalPrice: pkg.originalPrice,
      image: pkg.image,
      type: 'package',
      description: pkg.tagline,
    });
  };

  const handleQuickView = (pkg: typeof shopPackages[0]) => {
    openQuickView({
      id: `package-${pkg.id}`,
      name: pkg.name,
      description: pkg.tagline,
      price: pkg.price,
      originalPrice: pkg.originalPrice,
      image: pkg.image,
      type: 'package',
      badge: pkg.badge,
      details: [
        { label: 'Class', value: pkg.classIncluded, icon: 'ri-graduation-cap-line' },
        { label: 'Kit', value: pkg.kitIncluded, icon: 'ri-box-3-line' },
        { label: 'Manual', value: pkg.manualIncluded, icon: 'ri-book-open-line' },
        { label: 'You Save', value: `$${pkg.savings}`, icon: 'ri-price-tag-3-line' },
      ],
      extras: pkg.extras,
    });
  };

  const accentColors = [
    'from-amber-400 to-yellow-300',
    'from-rose-400 to-pink-300',
    'from-amber-300 to-yellow-200',
    'from-pink-400 to-rose-300',
    'from-red-400 to-rose-300',
  ];

  return (
    <section id="shop-packages" className="py-24 bg-stone-950 scroll-mt-20 relative overflow-hidden">
      <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-rose-500/5 rounded-full blur-3xl"></div>
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent"></div>

      <div ref={ref} className="relative z-10 max-w-7xl mx-auto px-6">
        <div className={`flex flex-col lg:flex-row lg:items-end lg:justify-between mb-14 gap-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-400 flex items-center justify-center shadow-lg shadow-amber-500/20">
                <i className="ri-gift-line text-white"></i>
              </div>
              <span className="text-amber-400 text-xs font-semibold tracking-[0.2em] uppercase">Exclusive Bundles</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-display font-normal text-white">
              Special <span className="italic text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-100 to-amber-300">Packages</span>
            </h2>
          </div>
          <p className="text-white/50 max-w-md leading-relaxed text-sm">
            Save big when you combine classes with premium cocktail kits and training manuals.
          </p>
        </div>

        {/* Featured Package */}
        <div className={`mb-10 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-product-shop>
          <div className="relative bg-gradient-to-br from-white/[0.08] to-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 overflow-hidden group">
            <div className="grid lg:grid-cols-2 gap-0">
              <div className="relative h-72 lg:h-auto overflow-hidden">
                <img src={shopPackages[2].image} alt={shopPackages[2].name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/60 hidden lg:block"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent lg:hidden"></div>
                <div className="absolute top-5 left-5">
                  <span className="px-3 py-1 bg-gradient-to-r from-amber-400 to-yellow-300 text-amber-900 text-xs font-bold rounded-full uppercase tracking-wider">{shopPackages[2].badge}</span>
                </div>
              </div>
              <div className="p-8 lg:p-10 flex flex-col justify-center">
                <p className="text-amber-400/70 text-sm tracking-widest uppercase mb-2">{shopPackages[2].tagline}</p>
                <h3 className="text-3xl lg:text-4xl font-serif font-bold text-white mb-4">{shopPackages[2].name}</h3>
                <div className="space-y-2.5 mb-5">
                  {[
                    { icon: 'ri-graduation-cap-line', text: shopPackages[2].classIncluded },
                    { icon: 'ri-box-3-line', text: shopPackages[2].kitIncluded },
                    { icon: 'ri-book-open-line', text: shopPackages[2].manualIncluded },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <div className="w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} text-amber-400 text-[10px]`}></i>
                      </div>
                      <p className="text-white/70 text-sm">{item.text}</p>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1.5 mb-6">
                  {shopPackages[2].extras.map((extra, i) => (
                    <span key={i} className="px-2.5 py-1 bg-white/5 border border-white/10 rounded-full text-white/60 text-[10px]">{extra}</span>
                  ))}
                </div>
                <div className="flex items-end gap-3 mb-5">
                  <span className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-yellow-200">${shopPackages[2].price}</span>
                  <span className="text-white/30 line-through text-lg mb-0.5">${shopPackages[2].originalPrice}</span>
                  <span className="px-2.5 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs font-semibold rounded-full mb-0.5">Save ${shopPackages[2].savings}</span>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => handleAdd(shopPackages[2])} className="px-7 py-3 bg-gradient-to-r from-amber-400 to-yellow-300 text-amber-900 font-bold rounded-full hover:from-amber-300 hover:to-yellow-200 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/20 flex items-center gap-2">
                    <i className="ri-shopping-cart-line"></i>Add to Cart
                  </button>
                  <button onClick={() => handleQuickView(shopPackages[2])} className="px-5 py-3 bg-white/10 border border-white/20 text-white rounded-full hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2">
                    <i className="ri-eye-line"></i>Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Other Packages Grid */}
        <div className={`grid md:grid-cols-2 gap-5 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {shopPackages.filter((_, i) => i !== 2).map((pkg, idx) => (
            <div key={pkg.id} className="group relative bg-gradient-to-br from-white/[0.06] to-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden hover:border-amber-500/30 transition-all duration-500">
              <div className="flex flex-col sm:flex-row">
                <div className="relative w-full sm:w-44 h-48 sm:h-auto flex-shrink-0 overflow-hidden">
                  <img src={pkg.image} alt={pkg.name} className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/40 hidden sm:block"></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent sm:hidden"></div>
                  <div className="absolute top-3 left-3">
                    <span className={`px-2.5 py-1 bg-gradient-to-r ${accentColors[idx]} text-stone-900 text-[10px] font-bold rounded-full uppercase tracking-wider`}>{pkg.badge}</span>
                  </div>
                </div>
                <div className="flex-1 p-5">
                  <p className="text-amber-400/60 text-[10px] tracking-widest uppercase mb-1">{pkg.tagline}</p>
                  <h3 className="text-lg font-serif font-bold text-white mb-2">{pkg.name}</h3>
                  <div className="space-y-1.5 mb-3">
                    <div className="flex items-center gap-1.5"><i className="ri-graduation-cap-line text-amber-400/70 text-xs"></i><p className="text-white/50 text-[11px]">{pkg.classIncluded}</p></div>
                    <div className="flex items-center gap-1.5"><i className="ri-box-3-line text-amber-400/70 text-xs"></i><p className="text-white/50 text-[11px]">{pkg.kitIncluded}</p></div>
                    <div className="flex items-center gap-1.5"><i className="ri-book-open-line text-amber-400/70 text-xs"></i><p className="text-white/50 text-[11px]">{pkg.manualIncluded}</p></div>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-white/5">
                    <div className="flex items-end gap-2">
                      <span className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-yellow-200">${pkg.price}</span>
                      <span className="text-white/25 line-through text-xs mb-0.5">${pkg.originalPrice}</span>
                    </div>
                    <button onClick={() => handleAdd(pkg)} className={`px-4 py-1.5 bg-gradient-to-r ${accentColors[idx]} text-stone-900 text-xs font-bold rounded-full hover:scale-105 transition-all cursor-pointer whitespace-nowrap shadow-lg flex items-center gap-1`}>
                      <i className="ri-shopping-cart-line text-xs"></i>Add to Cart
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
